import { cn } from "@core/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ComponentProps } from "react";

const radioStyles = cva(
  [
    "relative",
    "cursor-pointer",
    "flex",
    "items-center",
    "justify-center",
    "transition-all",
    "duration-200",
    "ease-in-out",
    "w-4",
    "h-4",
    "rounded-full",
  ],
  {
    variants: {
      variant: {
        primary:
          "border-[1px] bg-white border-neutral-200 hover:bg-neutral-50 hover:border-neutral-200",
      },
      state: {
        checked: "border-primary-800 hover:border-primary-800",
        error:
          "bg-accent-red-50 border-accent-red-800 hover:bg-accent-red-50 hover:border-accent-red-800",
        default: "",
      },
      proportions: {
        lg: "h-5 w-5",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);

type RadioProps = ComponentProps<"input"> &
  VariantProps<typeof radioStyles> & {
    onChange?: (checked: boolean) => void;
    onClickAction?: () => void;
  };

const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      variant,
      className,
      state,
      proportions,
      onChange,
      onClickAction,
      disabled,
      checked,
      ...props
    },
    ref,
  ) => {
    const handleClick = () => {
      if (disabled) return;
      onChange?.(true);
      onClickAction?.();
    };

    const handleNativeChange = (_e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      onChange?.(true);
      onClickAction?.();
    };

    return (
      <div className="flex items-center gap-2">
        <div
          className={cn(
            radioStyles({
              variant,
              state: checked
                ? "checked"
                : state === "error"
                  ? "error"
                  : "default",
              proportions,
            }),
            disabled && "opacity-50 cursor-not-allowed pointer-events-none",
            className,
          )}
          onClick={handleClick}
        >
          <input
            ref={ref}
            type="radio"
            onChange={handleNativeChange}
            className="hidden"
            disabled={disabled}
            checked={checked}
            {...props}
          />
          {checked && !disabled && (
            <div className="h-2 w-2 rounded-full bg-primary-800"></div>
          )}
        </div>
      </div>
    );
  },
);

Radio.displayName = "Radio";

export default Radio;
